// @src/pre-deployment/src/components/TrackingService/services/MasteryService.js v2.0
// - Migrated from src/pre-deployment/src/tracking/mastery.js
// - Part of TrackingService component migration
// - Enhanced with ErrorHandling integration via silentTrack options

import { getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';

import { getMasteryRef } from '../utils/PathBuilders.js';

import { silentTrack } from './SilentTrackService.js';

/**
 * MasteryService - Manages Mastery documents in Firestore
 * Tracks item-level exposure and mastery for vocabulary, grammar, dialogue, and comprehension content.
 */
export const MasteryService = {
    /**
     * Initialize Mastery document with discovered content
     * @param {string} userId - User ID
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @param {Object} [contentData={}] - Content data with vocabulary and grammar arrays
     * @returns {Promise<boolean|null>} True if created/exists, null on error
     */
    async initializeDocument(userId, level, chapter, contentData = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);
            const snap = await getDoc(masteryRef);

            if (!snap.exists()) {
                console.log(`Initializing Mastery: Level ${level} Chapter ${chapter}`);

                // Initialize vocabulary mastery
                const vocabularyMastery = {};
                if (contentData.vocabulary) {
                    contentData.vocabulary.forEach(item => {
                        vocabularyMastery[item.id] = {
                            isExposed: false,
                            exposureTime: null,
                            whereExposed: [],
                            learning: {},
                            practice: {}
                        };
                    });
                }

                // Initialize grammar mastery
                const grammarMastery = {};
                if (contentData.grammar) {
                    contentData.grammar.forEach(item => {
                        grammarMastery[item.id] = {
                            isExposed: false,
                            exposureTime: null,
                            whereExposed: [],
                            learning: {},
                            practice: {}
                        };
                    });
                }

                await setDoc(masteryRef, {
                    vocabulary: vocabularyMastery,
                    grammar: grammarMastery
                });
            }
            return true;
        }, 'initialize_mastery', {
            componentName: 'MasteryService',
            severity: 'high',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Mark vocabulary as exposed
     * @param {string} userId - User ID
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @param {string} wordId - Vocabulary item ID
     * @param {string} moduleKey - Module where exposed
     * @param {Object} [_dataParameters={}] - Additional parameters (unused, for API consistency)
     * @returns {Promise<void|null>} null on error
     */
    async markVocabularyExposed(userId, level, chapter, wordId, moduleKey, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);
            const snap = await getDoc(masteryRef);

            if (snap.exists() && snap.data().vocabulary[wordId]) {
                const currentData = snap.data().vocabulary[wordId];

                // Only mark if not already exposed
                if (!currentData.isExposed) {
                    await updateDoc(masteryRef, {
                        [`vocabulary.${wordId}.isExposed`]: true,
                        [`vocabulary.${wordId}.exposureTime`]: new Date().toISOString(),
                        [`vocabulary.${wordId}.whereExposed`]: arrayUnion(moduleKey)
                    });
                    console.log(`Vocabulary exposed: ${wordId} in ${moduleKey}`);
                }
            }
        }, `mark_vocabulary_exposed_${wordId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Mark grammar as exposed
     * @param {string} userId - User ID
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @param {string} grammarId - Grammar item ID
     * @param {string} moduleKey - Module where exposed
     * @param {Object} [_dataParameters={}] - Additional parameters (unused, for API consistency)
     * @returns {Promise<void|null>} null on error
     */
    async markGrammarExposed(userId, level, chapter, grammarId, moduleKey, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);
            const snap = await getDoc(masteryRef);

            if (snap.exists() && snap.data().grammar[grammarId]) {
                const currentData = snap.data().grammar[grammarId];

                // Only mark if not already exposed
                if (!currentData.isExposed) {
                    await updateDoc(masteryRef, {
                        [`grammar.${grammarId}.isExposed`]: true,
                        [`grammar.${grammarId}.exposureTime`]: new Date().toISOString(),
                        [`grammar.${grammarId}.whereExposed`]: arrayUnion(moduleKey)
                    });
                    console.log(`Grammar exposed: ${grammarId} in ${moduleKey}`);
                }
            }
        }, `mark_grammar_exposed_${grammarId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Update vocabulary mastery
     * @param {string} userId - User ID
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @param {string} wordId - Vocabulary item ID
     * @param {string} moduleKey - Module where attempted
     * @param {Object} masteryData - Mastery data (isCorrect, etc.)
     * @param {Object} [_dataParameters={}] - Additional parameters (unused)
     * @returns {Promise<void|null>} null on error
     */
    async updateVocabularyMastery(userId, level, chapter, wordId, moduleKey, masteryData, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);

            const masteryUpdate = {
                [`vocabulary.${wordId}.learning.${moduleKey}`]: {
                    ...masteryData,
                    dateAttempted: new Date().toISOString()
                }
            };

            await updateDoc(masteryRef, masteryUpdate);
            console.log(`Vocabulary mastery updated: ${wordId} in ${moduleKey}`);
        }, `update_vocabulary_mastery_${wordId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Update grammar mastery
     * @param {string} userId - User ID
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @param {string} grammarId - Grammar item ID
     * @param {string} moduleKey - Module where attempted
     * @param {Object} masteryData - Mastery data (isCorrect, etc.)
     * @param {Object} [_dataParameters={}] - Additional parameters (unused)
     * @returns {Promise<void|null>} null on error
     */
    async updateGrammarMastery(userId, level, chapter, grammarId, moduleKey, masteryData, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);

            const masteryUpdate = {
                [`grammar.${grammarId}.learning.${moduleKey}`]: {
                    ...masteryData,
                    dateAttempted: new Date().toISOString()
                }
            };

            await updateDoc(masteryRef, masteryUpdate);
            console.log(`Grammar mastery updated: ${grammarId} in ${moduleKey}`);
        }, `update_grammar_mastery_${grammarId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Mark dialogue as exposed
     */
    async markDialogueExposed(userId, level, chapter, dialogueId, moduleKey, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);
            const snap = await getDoc(masteryRef);

            if (snap.exists() && snap.data().dialogue?.[dialogueId]) {
                const currentData = snap.data().dialogue[dialogueId];

                if (!currentData.isExposed) {
                    await updateDoc(masteryRef, {
                        [`dialogue.${dialogueId}.isExposed`]: true,
                        [`dialogue.${dialogueId}.exposureTime`]: new Date().toISOString(),
                        [`dialogue.${dialogueId}.whereExposed`]: arrayUnion(moduleKey)
                    });
                    console.log(`Dialogue exposed: ${dialogueId} in ${moduleKey}`);
                }
            }
        }, `mark_dialogue_exposed_${dialogueId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Update dialogue mastery
     */
    async updateDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);

            const masteryUpdate = {
                [`dialogue.${dialogueId}.learning.${moduleKey}`]: {
                    ...masteryData,
                    dateAttempted: new Date().toISOString()
                }
            };

            await updateDoc(masteryRef, masteryUpdate);
            console.log(`Dialogue mastery updated: ${dialogueId} in ${moduleKey}`);
        }, `update_dialogue_mastery_${dialogueId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Mark intro dialogue as exposed
     */
    async markIntroDialogueExposed(userId, level, chapter, dialogueId, moduleKey, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);
            const snap = await getDoc(masteryRef);

            if (snap.exists() && snap.data().introDialogue?.[dialogueId]) {
                const currentData = snap.data().introDialogue[dialogueId];

                if (!currentData.isExposed) {
                    await updateDoc(masteryRef, {
                        [`introDialogue.${dialogueId}.isExposed`]: true,
                        [`introDialogue.${dialogueId}.exposureTime`]: new Date().toISOString(),
                        [`introDialogue.${dialogueId}.whereExposed`]: arrayUnion(moduleKey)
                    });
                    console.log(`Intro dialogue exposed: ${dialogueId} in ${moduleKey}`);
                }
            }
        }, `mark_intro_dialogue_exposed_${dialogueId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Update intro dialogue mastery
     */
    async updateIntroDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);

            const masteryUpdate = {
                [`introDialogue.${dialogueId}.learning.${moduleKey}`]: {
                    ...masteryData,
                    dateAttempted: new Date().toISOString()
                }
            };

            await updateDoc(masteryRef, masteryUpdate);
            console.log(`Intro dialogue mastery updated: ${dialogueId} in ${moduleKey}`);
        }, `update_intro_dialogue_mastery_${dialogueId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Mark conclusion dialogue as exposed
     */
    async markConclusionDialogueExposed(userId, level, chapter, dialogueId, moduleKey, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);
            const snap = await getDoc(masteryRef);

            if (snap.exists() && snap.data().conclusionDialogue?.[dialogueId]) {
                const currentData = snap.data().conclusionDialogue[dialogueId];

                if (!currentData.isExposed) {
                    await updateDoc(masteryRef, {
                        [`conclusionDialogue.${dialogueId}.isExposed`]: true,
                        [`conclusionDialogue.${dialogueId}.exposureTime`]: new Date().toISOString(),
                        [`conclusionDialogue.${dialogueId}.whereExposed`]: arrayUnion(moduleKey)
                    });
                    console.log(`Conclusion dialogue exposed: ${dialogueId} in ${moduleKey}`);
                }
            }
        }, `mark_conclusion_dialogue_exposed_${dialogueId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Update conclusion dialogue mastery
     */
    async updateConclusionDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);

            const masteryUpdate = {
                [`conclusionDialogue.${dialogueId}.learning.${moduleKey}`]: {
                    ...masteryData,
                    dateAttempted: new Date().toISOString()
                }
            };

            await updateDoc(masteryRef, masteryUpdate);
            console.log(`Conclusion dialogue mastery updated: ${dialogueId} in ${moduleKey}`);
        }, `update_conclusion_dialogue_mastery_${dialogueId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Mark comprehension as exposed
     */
    async markComprehensionExposed(userId, level, chapter, comprehensionId, moduleKey, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);
            const snap = await getDoc(masteryRef);

            if (snap.exists() && snap.data().comprehension?.[comprehensionId]) {
                const currentData = snap.data().comprehension[comprehensionId];

                if (!currentData.isExposed) {
                    await updateDoc(masteryRef, {
                        [`comprehension.${comprehensionId}.isExposed`]: true,
                        [`comprehension.${comprehensionId}.exposureTime`]: new Date().toISOString(),
                        [`comprehension.${comprehensionId}.whereExposed`]: arrayUnion(moduleKey)
                    });
                    console.log(`Comprehension exposed: ${comprehensionId} in ${moduleKey}`);
                }
            }
        }, `mark_comprehension_exposed_${comprehensionId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Update comprehension mastery
     */
    async updateComprehensionMastery(userId, level, chapter, comprehensionId, moduleKey, masteryData, _dataParameters = {}) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);

            const masteryUpdate = {
                [`comprehension.${comprehensionId}.learning.${moduleKey}`]: {
                    ...masteryData,
                    dateAttempted: new Date().toISOString()
                }
            };

            await updateDoc(masteryRef, masteryUpdate);
            console.log(`Comprehension mastery updated: ${comprehensionId} in ${moduleKey}`);
        }, `update_comprehension_mastery_${comprehensionId}`, {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    },

    /**
     * Get Mastery data
     * @param {string} userId - User ID
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @returns {Promise<Object|null>} Mastery data or null if not exists/error
     */
    async getMasteryData(userId, level, chapter) {
        const firebasePath = `users/${userId}/level_${level}/chapter_${chapter}/Learn/Mastery`;

        return silentTrack(async () => {
            const masteryRef = getMasteryRef(userId, level, chapter);
            const snap = await getDoc(masteryRef);
            return snap.exists() ? snap.data() : null;
        }, 'get_mastery_data', {
            componentName: 'MasteryService',
            severity: 'low',
            level: level,
            chapter: chapter,
            firebasePath: firebasePath
        });
    }
};

export default MasteryService;
